import { useCallback, useEffect } from "react";
import * as CONSTANTS from "../../constants";
import {
  parseHashPathAndQuery,
  removeMyLearningFilterParams,
  replaceHashPreservingPath,
} from "./myLearningFilterUrl";

export const useMyLearningFilterHandlers = ({
  updateState,
  state,
  contextFilter,
  mode,
  fetchMyLearningData,
  applyFilter,
  setStatus,
  setContextSortBy,
}) => {
  const setFilterStatus = useCallback(
    (updatedStatus) => {
      localStorage.setItem(CONSTANTS.STORAGE_KEY_FILTER_STATUS, updatedStatus);

      setStatus(updatedStatus);

      updateState((prevState) => ({
        filter: { ...prevState.filter, status: updatedStatus },
      }));
    },
    [setStatus, updateState],
  );

  const setSortBy = useCallback(
    (sortBy) => {
      updateState((prevState) => ({
        filter: { ...prevState.filter, sortBy },
      }));
      setContextSortBy(sortBy);
    },
    [setContextSortBy, updateState],
  );

  const setSearchText = useCallback(
    (searchText) => {
      updateState((prevState) => ({
        filter: { ...prevState.filter, searchText },
      }));
    },
    [updateState],
  );

  const onSearch = useCallback(
    (searchText = state.filter.searchText) => {
      const fetchFiltredContent = true;
      fetchMyLearningData(
        {
          searchText,
          selectedTags: state.filter.selectedTags,
          sortBy: state.filter.sortBy,
          mode,
          filter: state.filter.filter,
          categoryId: state.filter.categoryId,
          displayAdminCourses: state.filter.displayAdminCourses,
          status: state.filter.status,
        },
        fetchFiltredContent,
      );
    },
    [state.filter, fetchMyLearningData, mode],
  );

  const setTagFilter = useCallback(
    (no, value) => {
      updateState((prevState) => ({
        filter: {
          ...prevState.filter,
          selectedTags: [
            ...prevState.filter.selectedTags.slice(0, no),
            value,
            ...prevState.filter.selectedTags.slice(no + 1),
          ],
        },
      }));

      const updatedSelectedTags = [
        ...state.filter.selectedTags.slice(0, no),
        value,
        ...state.filter.selectedTags.slice(no + 1),
      ];

      const fetchFiltredContent = true;
      fetchMyLearningData(
        {
          searchText: state.filter.searchText,
          selectedTags: updatedSelectedTags,
          sortBy: state.filter.sortBy,
          mode,
          filter: state.filter.filter,
          categoryId: state.filter.categoryId,
          displayAdminCourses: state.filter.displayAdminCourses,
          status: state.filter.status,
        },
        fetchFiltredContent,
      );
    },
    [fetchMyLearningData, state.filter, mode, updateState],
  );

  const setCategoryId = useCallback(
    (rawCategoryId) => {
      const categoryId = parseInt(rawCategoryId, 10) || 0;
      updateState((prevState) => ({
        filter: { ...prevState.filter, categoryId },
      }));
      applyFilter({
        ...contextFilter,
        selectedTags: state.filter.selectedTags,
        categoryId,
        searchText: state.filter.searchText,
        sortBy: state.filter.sortBy,
        displayAdminCourses: state.filter.displayAdminCourses,
        status: state.filter.status,
        mode,
      });
    },
    [applyFilter, contextFilter, state.filter, mode, updateState],
  );

  const setDisplayAdminCourses = useCallback(
    (value) => {
      localStorage.setItem(CONSTANTS.STORAGE_KEY_ADMIN_TOGGLE, value);
      updateState((prevState) => ({
        filter: {
          ...prevState.filter,
          displayAdminCourses: value,
        },
      }));

      const hasActiveTags =
        Array.isArray(state.filter.selectedTags) &&
        state.filter.selectedTags.some((t) => t !== 0);

      const hasActiveSearch =
        state.filter.searchText && state.filter.searchText.length >= 2;

      if (hasActiveTags || hasActiveSearch) {
        const fetchFiltredContent = true;
        fetchMyLearningData(
          {
            searchText: state.filter.searchText,
            selectedTags: state.filter.selectedTags,
            sortBy: state.filter.sortBy,
            mode,
            filter: state.filter.filter,
            categoryId: state.filter.categoryId,
            displayAdminCourses: value,
            status: state.filter.status,
          },
          fetchFiltredContent,
        );
      } else {
        applyFilter({
          ...contextFilter,
          selectedTags: state.filter.selectedTags,
          categoryId: state.filter.categoryId,
          searchText: state.filter.searchText,
          sortBy: state.filter.sortBy,
          displayAdminCourses: value,
          status: state.filter.status,
          mode,
        });
      }
    },
    [
      applyFilter,
      fetchMyLearningData,
      contextFilter,
      state.filter,
      mode,
      updateState,
    ],
  );

  useEffect(() => {
    const storedAdminToggle = localStorage.getItem(
      CONSTANTS.STORAGE_KEY_ADMIN_TOGGLE,
    );
    if (storedAdminToggle === null) {
      return;
    }
    const storedValue = storedAdminToggle === "true";
    if (storedValue !== state.filter.displayAdminCourses) {
      setDisplayAdminCourses(storedValue);
    }
  }, [setDisplayAdminCourses, state.filter.displayAdminCourses]);

  const toggleDrawer = useCallback(() => {
    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        drawerOpen: !prevState.filter.drawerOpen,
      },
    }));
  }, [updateState]);

  const closeDrawer = useCallback(() => {
    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        drawerOpen: false,
      },
    }));
  }, [updateState]);

  const handleClearFilter = useCallback(() => {
    const currentLength = state.filter.selectedTags.length;
    const resetSelectedTags = new Array(currentLength).fill(0);

    const resetFilter = {
      searchText: "",
      selectedTags: resetSelectedTags,
      categoryId: 0,
      sortBy: state.filter.sortBy,
      mode,
      status: CONSTANTS.PROGRESS_ALL,
      displayAdminCourses: false,
    };

    updateState((prevState) => ({
      filter: {
        ...prevState.filter,
        ...resetFilter,
      },
    }));

    const fetchFilteredContent = false;
    fetchMyLearningData(
      {
        ...resetFilter,
        selectedTags: resetSelectedTags.map((v) => Number(v)),
      },
      fetchFilteredContent,
    );

    localStorage.setItem(
      CONSTANTS.STORAGE_KEY_FILTER_STATUS,
      CONSTANTS.PROGRESS_ALL,
    );
    localStorage.setItem(CONSTANTS.STORAGE_KEY_ADMIN_TOGGLE, "false");

    const { params } = parseHashPathAndQuery();
    removeMyLearningFilterParams(params);
    replaceHashPreservingPath(params);
  }, [fetchMyLearningData, state.filter.sortBy, mode, updateState]);

  return {
    setFilterStatus,
    setSortBy,
    setSearchText,
    onSearch,
    setTagFilter,
    setCategoryId,
    setDisplayAdminCourses,
    toggleDrawer,
    closeDrawer,
    handleClearFilter,
  };
};
